import { Box, Typography } from '@mui/material';
import React, { useCallback, useState, memo, useRef, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { TouchBackend } from 'react-dnd-touch-backend';
import { isMobile } from 'react-device-detect';
import { colors } from 'common/constants';
import { common } from '@mui/material/colors';
import PropTypes from 'prop-types';

interface Option {
    id: string;
    label: string;
    position?: string | number | null;
}

interface Props {
    name: string;
    options: Option[];
}

interface DragItem {
    index: number;
    type: string;
}

const DraggableItem: React.FC<{
    item: Option;
    index: number;
    moveItem: (dragIndex: number, hoverIndex: number) => void;
}> = memo(({ item, index, moveItem }) => {
    const ref = useRef<HTMLDivElement>(null);

    const [, drop] = useDrop<DragItem>({
        accept: 'option',
        hover(draggedItem, monitor) {
            if (!ref.current) return;
            const dragIndex = draggedItem.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const hoverBoundingRect = ref.current.getBoundingClientRect();
            const hoverMiddleY =
                (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            if (!clientOffset) return;
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            moveItem(dragIndex, hoverIndex);
            draggedItem.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag({
        type: 'option',
        item: { index },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    drag(drop(ref));

    return (
        <Box
            ref={ref}
            sx={{
                opacity: isDragging ? 0.5 : 1,
                backgroundColor: common.white,
                border: `1px solid ${colors.main}`,
                minHeight: '65px',
                cursor: 'move',
                display: 'flex',
                alignItems: 'center',
                padding: '0 30px',
                justifyContent: 'space-between',
                color: '#000',
                fontSize: '20px',
                fontWeight: 500,
                transition: 'transform 0.2s ease-in-out',
                boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
            }}
        >
            <Box>{item.label}</Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    alignItems: 'center',
                }}
            >
                <SwapVertIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                <Box>{item.position ?? ''}</Box>
            </Box>
        </Box>
    );
});
DraggableItem.displayName = 'DraggableItem';

DraggableItem.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
        position: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    index: PropTypes.number.isRequired,
    moveItem: PropTypes.func.isRequired,
};

const QuestionRank: React.FC<Props> = ({ name, options }) => {
    const { setValue, watch } = useFormContext();
    const currentValue = watch(name) as Option[] | undefined;

    // local state is the source of truth for UI
    const [sortedOptions, setSortedOptions] = useState<Option[]>(() =>
        (Array.isArray(currentValue) && currentValue.length > 0
            ? currentValue
            : options || []
        ).map((it, idx) => ({
            ...it,
            position: it.position ?? idx + 1,
        }))
    );

    useEffect(() => {
        if (Array.isArray(currentValue) && currentValue.length > 0) {
            setSortedOptions(
                currentValue.map((it, idx) => ({
                    ...it,
                    position: it.position ?? idx + 1,
                }))
            );
            return;
        }
        if (!currentValue && Array.isArray(options) && options.length > 0) {
            setValue(
                name,
                options.map((it, idx) => ({
                    ...it,
                    position: it.position ?? idx + 1,
                })),
                { shouldDirty: false, shouldTouch: false }
            );
        }
    }, [currentValue, options, name, setValue]);

    const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
        setSortedOptions((prevOptions) => {
            const updatedOptions = [...prevOptions];
            const [removed] = updatedOptions.splice(dragIndex, 1);
            updatedOptions.splice(hoverIndex, 0, removed);
            return updatedOptions.map((it, idx) => ({
                ...it,
                position: idx + 1,
            }));
        });
    }, []);

    useEffect(() => {
        const formJson = JSON.stringify(currentValue ?? []);
        const localJson = JSON.stringify(sortedOptions);
        if (formJson !== localJson) {
            setValue(name, sortedOptions, {
                shouldDirty: true,
                shouldTouch: true,
            });
        }
    }, [sortedOptions, name, setValue, currentValue]);

    return (
        <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
            <Typography
                sx={{
                    color: (theme) => theme.palette.common.white,
                    marginBottom: '1rem',
                    display: ['block', 'none'],
                }}
            >
                Kéo thả để sắp xếp vị trí
            </Typography>
            {sortedOptions.map((item: Option, index: number) => (
                <DraggableItem
                    key={String(item.id)}
                    item={item}
                    index={index}
                    moveItem={moveItem}
                />
            ))}
        </DndProvider>
    );
};

export default QuestionRank;
